export const AMOUNT_DECIMAL_PLACES = 2;
export const PRECISION_FACTOR = 10 ** AMOUNT_DECIMAL_PLACES;

const toFloat = (value) => {
    const normalized = String(value ?? '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const round = (value) => {
    return Math.round(value * PRECISION_FACTOR) / PRECISION_FACTOR;
};

const parseQuantity = (quantity) => {
    if (!quantity) {
        return 1;
    }
    const parsed = parseFloat(quantity);
    return Number.isNaN(parsed) ? 1 : parsed;
};

const computeTaxAmountForRate = (products, taxId, taxRate) => {
    const rate = Number(taxRate);

    const totalWithoutTaxInCents = products
        .filter((product) => product.taxRateId === taxId)
        .reduce((sum, product) => {
            const qty = parseQuantity(product.quantity);
            const priceWithoutTax = toFloat(product.priceWithoutTax || 0);
            return sum + Math.round(priceWithoutTax * qty * PRECISION_FACTOR);
        }, 0);

    return Math.round((totalWithoutTaxInCents * rate) / 100);
};

export const calculateTotalWithoutTax = (products, keepDefaultValues, defaultTotal) => {
    if (keepDefaultValues) {
        return defaultTotal;
    }

    const total = products.reduce((sum, { priceWithoutTax, quantity }) => {
        const qty = parseQuantity(quantity);
        const amount = toFloat(priceWithoutTax);
        const amountInCents = Math.round(amount * qty * PRECISION_FACTOR);
        return sum + amountInCents / PRECISION_FACTOR;
    }, 0);

    return round(total);
};

export const calculateTotalWithTax = (totalWithoutTax, totalTax) => {
    const withoutTaxInCents = Math.round(toFloat(totalWithoutTax) * PRECISION_FACTOR);
    const taxInCents = Math.round(toFloat(totalTax) * PRECISION_FACTOR);

    return round((withoutTaxInCents + taxInCents) / PRECISION_FACTOR);
};

export const calculateTotalTax = (products, taxRows) => {
    let overrideTaxInCents = 0;
    const nonOverriddenProducts = [];

    for (const product of products) {
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideTaxInCents += Math.round(parsed * PRECISION_FACTOR);
                continue;
            }
        }
        nonOverriddenProducts.push(product);
    }

    const rateTaxInCents = taxRows.reduce((sum, taxItem) => {
        return sum + computeTaxAmountForRate(nonOverriddenProducts, taxItem.id, taxItem.rate);
    }, 0);

    return round((overrideTaxInCents + rateTaxInCents) / PRECISION_FACTOR);
};

export const getUniqueTaxRows = (products, taxList) => {
    const seenIds = new Set();
    const taxes = [];

    for (const product of products) {
        if (
            product.taxRateId === null ||
            product.taxRateId === undefined ||
            seenIds.has(product.taxRateId)
        ) {
            continue;
        }
        const taxItem = taxList.find((tax) => tax.id === product.taxRateId);
        if (taxItem) {
            seenIds.add(taxItem.id);
            taxes.push(taxItem);
        }
    }

    return taxes;
};

export const calculateTotalByTax = (products, taxId, taxRate) => {
    let overrideInCents = 0;
    const nonOverridden = [];

    for (const product of products) {
        if (product.taxRateId !== taxId) {
            continue;
        }
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideInCents += Math.round(parsed * PRECISION_FACTOR);
                continue;
            }
        }
        nonOverridden.push(product);
    }

    return round(
        (overrideInCents + computeTaxAmountForRate(nonOverridden, taxId, taxRate)) /
            PRECISION_FACTOR,
    );
};

const DEFAULT_EMPTY_PRODUCT = {
    description: '',
    quantity: 1,
    priceWithoutTax: null,
    unit: '',
    catalogueLink: null,
    analyticCode: '',
    stockOutDate: null,
    warehouseId: '',
    serialNumbers: '',
    taxAmountOverride: null,
    amountIncludingTaxOverride: null,
};

export const createEmptyProduct = ({
    defaultAccountingCode = null,
    defaultTaxRateId = null,
    supplierId = null,
    companyAccountingCode = null,
} = {}) => ({
    ...DEFAULT_EMPTY_PRODUCT,
    accountingCode: supplierId === null ? defaultAccountingCode : companyAccountingCode,
    taxRateId: defaultTaxRateId,
});

/**
 * Limits an amount input value to 2 decimal places.
 * Accepts both comma and dot as decimal separator.
 * @param {string|number} value - The input value
 * @returns {string} The value limited to 2 decimal places
 */
export const limitToTwoDecimals = (value) => {
    if (value === null || value === '' || value === '-') {
        return value;
    }
    const str = String(value);
    const separator = str.includes(',') ? ',' : '.';
    const separatorIndex = str.indexOf(separator);
    if (separatorIndex !== -1 && str.length - separatorIndex - 1 > 2) {
        return str.substring(0, separatorIndex + 3);
    }
    return str;
};

/**
 * Formats a field value to exactly 2 decimal places with comma separator.
 * @param {Object} object - The object containing the field
 * @param {string} field - The field name to format
 */
export const formatFieldToTwoDecimals = (object, field) => {
    const value = object[field];
    if (value !== null && value !== '') {
        const parsed = parseFloat(String(value).replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            object[field] = parsed.toFixed(2).replace('.', ',');
        }
    }
};

export const setProductDescriptionFromAccountingCode = (product, accountingCodes) => {
    const hasCustomDescription =
        product.description && !accountingCodes.some((code) => code.label === product.description);
    if (hasCustomDescription) {
        return;
    }

    const matchingCode = accountingCodes.find((code) => code.id === product.accountingCode);
    product.description = matchingCode?.label ?? '';
};
